import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Calendar, Search, Download, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/lib/api';

export default function LeaveBalancePage() {
  const { user } = useAuth();
  const canEdit = ['super_admin', 'admin', 'hr'].includes(user?.role);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [departments, setDepartments] = useState([]);

  const [editing, setEditing] = useState(null);
  const [annualDraft, setAnnualDraft] = useState('');
  const [sickDraft, setSickDraft] = useState('');
  const [annualUsedPreDraft, setAnnualUsedPreDraft] = useState('');
  const [sickUsedPreDraft, setSickUsedPreDraft] = useState('');
  const [casualUsedPreDraft, setCasualUsedPreDraft] = useState('');
  const [openingAsOfDraft, setOpeningAsOfDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const empRes = await api.get('/hr/leave-balance');
      setEmployees(empRes.data || []);
    } catch { setEmployees([]); }
    try {
      const deptRes = await api.get('/departments');
      setDepartments(deptRes.data || []);
    } catch { setDepartments([]); }
    setLoading(false);
  }

  const filtered = employees.filter(e => {
    const matchSearch = !search || (e.employee_name || '').toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === 'all' || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  function openEdit(emp) {
    setEditing(emp);
    setAnnualDraft(String(emp.annual_leave ?? ''));
    setSickDraft(String(emp.sick_leave ?? ''));
    setAnnualUsedPreDraft(String(emp.annual_used_pre_system ?? ''));
    setSickUsedPreDraft(String(emp.sick_used_pre_system ?? ''));
    setCasualUsedPreDraft(String(emp.casual_used_pre_system ?? ''));
    setOpeningAsOfDraft(emp.leave_opening_as_of || '2026-05-01');
  }

  async function saveEdit() {
    if (!editing) return;
    const empKey = editing.id || editing.employee_id;
    if (!empKey) return;
    try {
      setSaving(true);
      const payload = {
        annual_leave_balance: annualDraft === '' ? null : Number(annualDraft),
        sick_leave_balance: sickDraft === '' ? null : Number(sickDraft),
        annual_used_pre_system: annualUsedPreDraft === '' ? null : Number(annualUsedPreDraft),
        sick_used_pre_system: sickUsedPreDraft === '' ? null : Number(sickUsedPreDraft),
        casual_used_pre_system: casualUsedPreDraft === '' ? null : Number(casualUsedPreDraft),
        leave_opening_as_of: openingAsOfDraft || null,
      };
      await api.put(`/hr/employees/${empKey}/leave-balance`, payload);
      toast.success(`Updated leave balance for ${editing.employee_name}`);
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="leave-balance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leave Balance</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View and update leave entitlements for all employees
          </p>
        </div>
        <Button variant="outline" data-testid="export-leave-balance-btn">
          <Download className="h-4 w-4 mr-2" /> Export
        </Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search employee..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="leave-search" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[200px]" data-testid="dept-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map(d => (
              <SelectItem key={d.name || d._id || d} value={d.name || d}>{d.name || d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No Data</h3>
              <p className="text-muted-foreground text-sm">No leave balance records found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-center">Annual<br /><span className="text-[10px] text-muted-foreground">total / used / left</span></TableHead>
                  <TableHead className="text-center">Sick<br /><span className="text-[10px] text-muted-foreground">total / used / left</span></TableHead>
                  <TableHead className="text-center">Casual<br /><span className="text-[10px] text-muted-foreground">used</span></TableHead>
                  <TableHead className="text-center">Total<br />Used</TableHead>
                  <TableHead className="text-center">Remaining</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((emp, i) => {
                  const annualLeft = Math.max(0, (emp.annual_leave || 0) - (emp.annual_used || 0));
                  const sickLeft = Math.max(0, (emp.sick_leave || 0) - (emp.sick_used || 0));
                  return (
                    <TableRow key={emp.employee_id || i} data-testid={`leave-row-${i}`}>
                      <TableCell className="font-medium">{emp.employee_name || 'N/A'}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell className="text-center font-mono text-xs">
                        {emp.annual_leave ?? '-'} / {emp.annual_used ?? 0} / <span className="text-emerald-600">{annualLeft}</span>
                        {emp.annual_used_pre_system > 0 && (
                          <p className="text-[9px] text-amber-600">pre: {emp.annual_used_pre_system}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">
                        {emp.sick_leave ?? '-'} / {emp.sick_used ?? 0} / <span className="text-emerald-600">{sickLeft}</span>
                        {emp.sick_used_pre_system > 0 && (
                          <p className="text-[9px] text-amber-600">pre: {emp.sick_used_pre_system}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">{emp.casual_leave ?? 0}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{emp.total_used ?? 0}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={
                          (emp.remaining ?? 0) <= 2 ? 'bg-red-500/10 text-red-600' :
                          (emp.remaining ?? 0) <= 5 ? 'bg-yellow-500/10 text-yellow-600' :
                          'bg-green-500/10 text-green-600'
                        }>
                          {emp.remaining ?? '-'}
                        </Badge>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(emp)}
                            data-testid={`edit-leave-balance-${i}`}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent data-testid="edit-leave-balance-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Leave Balance</DialogTitle>
            <DialogDescription>
              {editing ? `Set entitlement and pre-system used leaves for ${editing.employee_name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* Yearly entitlement */}
            <div className="border rounded-md p-3 bg-muted/20">
              <p className="text-xs font-semibold mb-2">Yearly Entitlement</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Annual Leave (days/yr)</Label>
                  <Input
                    type="number" min="0" max="365" step="0.5"
                    value={annualDraft}
                    onChange={e => setAnnualDraft(e.target.value)}
                    data-testid="annual-leave-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sick Leave (days/yr)</Label>
                  <Input
                    type="number" min="0" max="365" step="0.5"
                    value={sickDraft}
                    onChange={e => setSickDraft(e.target.value)}
                    data-testid="sick-leave-input"
                  />
                </div>
              </div>
            </div>

            {/* Pre-system used (system go-live carry-forward) */}
            <div className="border-2 border-amber-500/40 bg-amber-500/5 rounded-md p-3">
              <p className="text-xs font-semibold mb-1">Leaves Used Before System Go-Live</p>
              <p className="text-[11px] text-muted-foreground mb-3">
                System goes live <b>May 1, 2026</b>. Enter leaves the employee has already taken between Jan 1 and Apr 30 so the remaining balance from May 1 onwards is accurate.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Annual Used (pre)</Label>
                  <Input
                    type="number" min="0" max="365" step="0.5"
                    value={annualUsedPreDraft}
                    placeholder="0"
                    onChange={e => setAnnualUsedPreDraft(e.target.value)}
                    data-testid="annual-used-pre-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Sick Used (pre)</Label>
                  <Input
                    type="number" min="0" max="365" step="0.5"
                    value={sickUsedPreDraft}
                    placeholder="0"
                    onChange={e => setSickUsedPreDraft(e.target.value)}
                    data-testid="sick-used-pre-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Casual Used (pre)</Label>
                  <Input
                    type="number" min="0" max="365" step="0.5"
                    value={casualUsedPreDraft}
                    placeholder="0"
                    onChange={e => setCasualUsedPreDraft(e.target.value)}
                    data-testid="casual-used-pre-input"
                  />
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <Label className="text-xs">Opening As-Of Date</Label>
                <Input
                  type="date"
                  value={openingAsOfDraft}
                  onChange={e => setOpeningAsOfDraft(e.target.value)}
                  data-testid="opening-as-of-input"
                />
                <p className="text-[10px] text-muted-foreground">Leaves logged in the system on/after this date count as "used since open".</p>
              </div>
            </div>

            {/* Live preview */}
            {editing && (
              <div className="border rounded-md p-3 bg-emerald-500/5">
                <p className="text-xs font-semibold mb-2">Live Remaining Preview</p>
                {(() => {
                  const aT = Number(annualDraft || editing.annual_leave || 0);
                  const sT = Number(sickDraft || editing.sick_leave || 0);
                  const aPre = Number(annualUsedPreDraft || 0);
                  const sPre = Number(sickUsedPreDraft || 0);
                  const aSys = Number(editing.annual_used_system || 0);
                  const sSys = Number(editing.sick_used_system || 0);
                  const aRem = Math.max(0, aT - aPre - aSys);
                  const sRem = Math.max(0, sT - sPre - sSys);
                  return (
                    <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                      <div>Annual remaining: <span className="font-semibold text-emerald-700">{aRem.toFixed(1)}</span></div>
                      <div>Sick remaining: <span className="font-semibold text-emerald-700">{sRem.toFixed(1)}</span></div>
                      <div className="text-[10px] text-muted-foreground col-span-2">
                        annual: {aT} − pre {aPre} − system {aSys} = {aRem.toFixed(1)}<br />
                        sick: {sT} − pre {sPre} − system {sSys} = {sRem.toFixed(1)}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-leave-balance-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
